import { supabase } from '../lib/supabase';
import { localDataEncryption } from './LocalDataEncryption';
import { errorLogger } from '../utils/errorLogger';

export interface UserDataItem {
  id: string;
  userId: string;
  type: 'condition' | 'symptom' | 'medication' | 'food' | 'device' | 'preference' | 'profile';
  data: any;
  createdAt: string;
  updatedAt: string;
  syncStatus: 'local_only' | 'synced' | 'pending_sync' | 'sync_failed';
  localBackup: boolean;
  cloudBackup: boolean;
}

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
  lastSyncTime: string;
}

export interface DataValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export class UserDataPersistenceService {
  private static instance: UserDataPersistenceService;
  private syncQueue: UserDataItem[] = [];
  private isSyncing = false;
  private lastSyncTime: string | null = null;

  private constructor() {
    this.initializeSync();
  }

  public static getInstance(): UserDataPersistenceService {
    if (!UserDataPersistenceService.instance) {
      UserDataPersistenceService.instance = new UserDataPersistenceService();
    }
    return UserDataPersistenceService.instance;
  }

  /**
   * Initialize sync process and load pending items
   */
  private async initializeSync(): Promise<void> {
    try {
      // Load pending sync items from localStorage
      const pendingItems = this.getPendingSyncItems();
      this.syncQueue = pendingItems;

      // Start background sync if there are pending items
      if (this.syncQueue.length > 0) {
        this.performBackgroundSync();
      }
    } catch (error) {
      errorLogger.logError(
        error instanceof Error ? error : new Error(String(error)),
        'UserDataPersistenceService',
        { action: 'initialize_sync' },
        'medium'
      );
    }
  }

  /**
   * Save user data to both localStorage and queue for Supabase sync
   */
  public async saveUserData(
    userId: string,
    type: UserDataItem['type'],
    data: any,
    options: {
      immediateSync?: boolean;
      encrypt?: boolean;
      backup?: boolean;
    } = {}
  ): Promise<string> {
    const dataId = crypto.randomUUID();
    const now = new Date().toISOString();

    try {
      // Create user data item
      const userDataItem: UserDataItem = {
        id: dataId,
        userId,
        type,
        data,
        createdAt: now,
        updatedAt: now,
        syncStatus: 'local_only',
        localBackup: true,
        cloudBackup: false
      };

      // Save to localStorage
      await this.saveToLocalStorage(userDataItem, options.encrypt);

      // Add to sync queue
      this.syncQueue.push(userDataItem);

      // Perform immediate sync if requested
      if (options.immediateSync) {
        await this.performBackgroundSync();
      }

      // Log successful save
      errorLogger.logError(
        new Error('Data saved successfully'), // Using error logger for success tracking
        'UserDataPersistenceService',
        {
          action: 'save_user_data',
          dataId,
          type,
          userId,
          immediateSync: options.immediateSync
        },
        'low'
      );

      return dataId;
    } catch (error) {
      errorLogger.logError(
        error instanceof Error ? error : new Error(String(error)),
        'UserDataPersistenceService',
        {
          action: 'save_user_data',
          dataId,
          type,
          userId
        },
        'high'
      );
      throw error;
    }
  }

  /**
   * Save data to localStorage with optional encryption
   */
  private async saveToLocalStorage(
    userDataItem: UserDataItem,
    encrypt: boolean = true
  ): Promise<void> {
    try {
      const storageKey = `zippyhealth_user_data_${userDataItem.id}`;
      
      if (encrypt) {
        // Use encryption service for sensitive data
        await localDataEncryption.storeEncryptedData(userDataItem, 'user_data');
      } else {
        // Store unencrypted for non-sensitive data
        localStorage.setItem(storageKey, JSON.stringify(userDataItem));
      }

      // Also save to backup location
      const backupKey = `zippyhealth_backup_${userDataItem.id}`;
      localStorage.setItem(backupKey, JSON.stringify(userDataItem));
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
      throw error;
    }
  }

  /**
   * Retrieve user data from localStorage
   */
  public async getUserData(dataId: string): Promise<UserDataItem | null> {
    try {
      // Try to get from encrypted storage first
      const encryptedData = await localDataEncryption.getEncryptedData(dataId, 'user_data');
      if (encryptedData) {
        return encryptedData as UserDataItem;
      }

      // Fallback to unencrypted storage
      const storageKey = `zippyhealth_user_data_${dataId}`;
      const item = localStorage.getItem(storageKey);
      
      if (item) {
        return JSON.parse(item) as UserDataItem;
      }

      return null;
    } catch (error) {
      errorLogger.logError(
        error instanceof Error ? error : new Error(String(error)),
        'UserDataPersistenceService',
        { action: 'get_user_data', dataId },
        'medium'
      );
      return null;
    }
  }

  /**
   * Get all user data of a specific type
   */
  public async getAllUserData(
    userId: string,
    type?: UserDataItem['type']
  ): Promise<UserDataItem[]> {
    const allData: UserDataItem[] = [];
    
    try {
      // Get all user data keys
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith('zippyhealth_user_data_') || 
        key.startsWith('zippyhealth_encrypted_user_data_')
      );

      for (const key of keys) {
        const dataId = key.replace('zippyhealth_user_data_', '').replace('zippyhealth_encrypted_user_data_', '');
        const userData = await this.getUserData(dataId);
        
        if (userData && userData.userId === userId && (!type || userData.type === type)) {
          allData.push(userData);
        }
      }

      // Sort by creation date (newest first)
      return allData.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } catch (error) {
      errorLogger.logError(
        error instanceof Error ? error : new Error(String(error)),
        'UserDataPersistenceService',
        { action: 'get_all_user_data', userId, type },
        'medium'
      );
      return [];
    }
  }

  /**
   * Sync data with Supabase
   */
  public async syncWithSupabase(): Promise<SyncResult> {
    if (this.isSyncing) {
      return {
        success: false,
        synced: 0,
        failed: 0,
        errors: ['Sync already in progress'],
        lastSyncTime: this.lastSyncTime || new Date().toISOString()
      };
    }

    this.isSyncing = true;
    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
      lastSyncTime: new Date().toISOString()
    };

    try {
      const pendingItems = this.syncQueue.filter(item => 
        item.syncStatus === 'local_only' || item.syncStatus === 'sync_failed'
      );

      for (const item of pendingItems) {
        try {
          const success = await this.syncItemToSupabase(item);
          if (success) {
            item.syncStatus = 'synced';
            item.updatedAt = new Date().toISOString();
            result.synced++;
          } else {
            item.syncStatus = 'sync_failed';
            result.failed++;
            result.errors.push(`Failed to sync item ${item.id}`);
          }
        } catch (error) {
          item.syncStatus = 'sync_failed';
          result.failed++;
          result.errors.push(`Error syncing item ${item.id}: ${error}`);
        }
      }

      // Update localStorage with sync status
      await this.updateSyncStatus(pendingItems);

      this.lastSyncTime = result.lastSyncTime;
      this.syncQueue = this.syncQueue.filter(item => item.syncStatus !== 'synced');

    } catch (error) {
      result.success = false;
      result.errors.push(`Sync failed: ${error}`);
      errorLogger.logError(
        error instanceof Error ? error : new Error(String(error)),
        'UserDataPersistenceService',
        { action: 'sync_with_supabase' },
        'high'
      );
    } finally {
      this.isSyncing = false;
    }

    return result;
  }

  /**
   * Sync individual item to Supabase
   */
  private async syncItemToSupabase(item: UserDataItem): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Map item type to Supabase table
      const tableMap: Record<UserDataItem['type'], string> = {
        condition: 'user_medical_conditions',
        symptom: 'symptoms',
        medication: 'medications',
        food: 'food_entries',
        device: 'device_data',
        preference: 'user_profiles',
        profile: 'user_profiles'
      };

      const tableName = tableMap[item.type];
      if (!tableName) {
        throw new Error(`Unknown data type: ${item.type}`);
      }

      // Prepare data for Supabase
      const supabaseData = this.prepareDataForSupabase(item);

      // Insert or update in Supabase
      const { error } = await supabase
        .from(tableName)
        .upsert(supabaseData, { onConflict: 'id' });

      if (error) {
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Failed to sync item to Supabase:', error);
      return false;
    }
  }

  /**
   * Prepare data for Supabase insertion
   */
  private prepareDataForSupabase(item: UserDataItem): any {
    const baseData = {
      id: item.id,
      user_id: item.userId,
      created_at: item.createdAt,
      updated_at: item.updatedAt
    };

    switch (item.type) {
      case 'condition':
        return {
          ...baseData,
          condition_id: item.data.conditionId,
          custom_condition_name: item.data.customName,
          severity: item.data.severity,
          diagnosed_date: item.data.diagnosedDate,
          status: item.data.status,
          notes: item.data.notes
        };
      case 'symptom':
        return {
          ...baseData,
          name: item.data.name,
          category: item.data.category,
          severity: item.data.severity,
          notes: item.data.notes,
          triggers: item.data.triggers,
          related_conditions: item.data.relatedConditions,
          ai_suggested: item.data.aiSuggested,
          recorded_at: item.data.recordedAt
        };
      case 'medication':
        return {
          ...baseData,
          name: item.data.name,
          dosage: item.data.dosage,
          frequency: item.data.frequency,
          instructions: item.data.instructions,
          prescribing_provider: item.data.prescribingProvider,
          start_date: item.data.startDate,
          end_date: item.data.endDate,
          is_active: item.data.isActive,
          adherence_percentage: item.data.adherencePercentage,
          ndc_code: item.data.ndcCode
        };
      case 'food':
        return {
          ...baseData,
          meal_type: item.data.mealType,
          food_name: item.data.foodName,
          brand: item.data.brand,
          serving_size: item.data.servingSize,
          serving_unit: item.data.servingUnit,
          macros: item.data.macros,
          usda_fdc_id: item.data.usdaFdcId,
          barcode: item.data.barcode,
          glucose_impact: item.data.glucoseImpact,
          logged_at: item.data.loggedAt
        };
      case 'device':
        return {
          ...baseData,
          device_id: item.data.deviceId,
          metric_name: item.data.metricName,
          metric_value: item.data.metricValue,
          metric_unit: item.data.metricUnit,
          recorded_at: item.data.recordedAt,
          source_timestamp: item.data.sourceTimestamp,
          metadata: item.data.metadata
        };
      case 'preference':
      case 'profile':
        return {
          ...baseData,
          date_of_birth: item.data.dateOfBirth,
          phone_number: item.data.phoneNumber,
          emergency_contact: item.data.emergencyContact,
          avatar_url: item.data.avatarUrl,
          preferred_providers: item.data.preferredProviders,
          data_storage_preference: item.data.dataStoragePreference,
          privacy_settings: item.data.privacySettings,
          consent_settings: item.data.consentSettings
        };
      default:
        throw new Error(`Unknown data type: ${item.type}`);
    }
  }

  /**
   * Update sync status in localStorage
   */
  private async updateSyncStatus(items: UserDataItem[]): Promise<void> {
    for (const item of items) {
      try {
        const storageKey = `zippyhealth_user_data_${item.id}`;
        localStorage.setItem(storageKey, JSON.stringify(item));
      } catch (error) {
        console.error('Failed to update sync status:', error);
      }
    }
  }

  /**
   * Get pending sync items from localStorage
   */
  private getPendingSyncItems(): UserDataItem[] {
    const pendingItems: UserDataItem[] = [];
    
    try {
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith('zippyhealth_user_data_')
      );

      for (const key of keys) {
        const item = localStorage.getItem(key);
        if (item) {
          const userDataItem = JSON.parse(item) as UserDataItem;
          if (userDataItem.syncStatus === 'local_only' || userDataItem.syncStatus === 'sync_failed') {
            pendingItems.push(userDataItem);
          }
        }
      }
    } catch (error) {
      console.error('Failed to get pending sync items:', error);
    }

    return pendingItems;
  }

  /**
   * Perform background sync
   */
  private async performBackgroundSync(): Promise<void> {
    if (this.isSyncing) return;

    try {
      await this.syncWithSupabase();
    } catch (error) {
      console.error('Background sync failed:', error);
    }
  }

  /**
   * Validate user data
   */
  public validateUserData(data: any, type: UserDataItem['type']): DataValidationResult {
    const result: DataValidationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    try {
      // Basic validation
      if (!data) {
        result.isValid = false;
        result.errors.push('Data is required');
        return result;
      }

      // Type-specific validation
      switch (type) {
        case 'condition':
          if (!data.name && !data.conditionId) {
            result.isValid = false;
            result.errors.push('Condition name or ID is required');
          }
          break;
        case 'symptom':
          if (!data.name) {
            result.isValid = false;
            result.errors.push('Symptom name is required');
          }
          if (data.severity && (data.severity < 1 || data.severity > 10)) {
            result.warnings.push('Symptom severity should be between 1 and 10');
          }
          break;
        case 'medication':
          if (!data.name) {
            result.isValid = false;
            result.errors.push('Medication name is required');
          }
          if (!data.dosage) {
            result.isValid = false;
            result.errors.push('Medication dosage is required');
          }
          break;
        case 'food':
          if (!data.foodName) {
            result.isValid = false;
            result.errors.push('Food name is required');
          }
          break;
        case 'device':
          if (!data.metricName || !data.metricValue) {
            result.isValid = false;
            result.errors.push('Device metric name and value are required');
          }
          break;
      }
    } catch (error) {
      result.isValid = false;
      result.errors.push(`Validation error: ${error}`);
    }

    return result;
  }

  /**
   * Get sync status and statistics
   */
  public getSyncStatus(): {
    pendingItems: number;
    syncedItems: number;
    failedItems: number;
    lastSyncTime: string | null;
    isSyncing: boolean;
  } {
    const pendingItems = this.syncQueue.filter(item => 
      item.syncStatus === 'local_only' || item.syncStatus === 'sync_failed'
    ).length;
    
    const syncedItems = this.syncQueue.filter(item => 
      item.syncStatus === 'synced'
    ).length;
    
    const failedItems = this.syncQueue.filter(item => 
      item.syncStatus === 'sync_failed'
    ).length;

    return {
      pendingItems,
      syncedItems,
      failedItems,
      lastSyncTime: this.lastSyncTime,
      isSyncing: this.isSyncing
    };
  }

  /**
   * Force immediate sync
   */
  public async forceSync(): Promise<SyncResult> {
    return await this.syncWithSupabase();
  }

  /**
   * Clear all user data
   */
  public async clearAllUserData(userId: string): Promise<boolean> {
    try {
      // Clear from localStorage
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('zippyhealth_user_data_') || key.startsWith('zippyhealth_backup_'))) {
          const item = localStorage.getItem(key);
          if (item) {
            const userDataItem = JSON.parse(item) as UserDataItem;
            if (userDataItem.userId === userId) {
              keysToRemove.push(key);
            }
          }
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Clear from sync queue
      this.syncQueue = this.syncQueue.filter(item => item.userId !== userId);

      return true;
    } catch (error) {
      errorLogger.logError(
        error instanceof Error ? error : new Error(String(error)),
        'UserDataPersistenceService',
        { action: 'clear_all_user_data', userId },
        'high'
      );
      return false;
    }
  }
}

// Export singleton instance
export const userDataPersistence = UserDataPersistenceService.getInstance(); 